import React from "react"
import { Link, navigate } from "gatsby"
import { ChevronRightIcon } from "@heroicons/react/solid"

import Navigation from "../common/navigation"
import Image from "../common/image"
import ScrollingCommunities from "../community/scrollingCommunities"

const HeroSection = ({
  navigation,
  featuredCommunities,
  communityTotalCount,
  isMobile,
}) => {
  return (
    <header className="bg-yellow-300">
      <div className="overflow-hidden lg:relative text-black">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="relative h-16 flex items-center justify-between lg:border-b lg:border-transparent">
            <div className="px-2 flex items-center lg:px-0">
              <Link to="/" className="flex-shrink-0">
                <Image
                  className="block h-10 w-auto"
                  fileName="icon.png"
                  alt="Hive Index Logo"
                  height="35"
                  width="31"
                />
              </Link>
              <nav className="ml-6 hidden sm:flex" aria-label="Primary navigation">
                <Navigation navigation={navigation} />
              </nav>
            </div>

            {/* Mobile quick-nav — visible only on small screens */}
            <nav className="flex sm:hidden items-center gap-1 pr-2" aria-label="Mobile navigation">
              <Link
                to="/topics/"
                className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-800 hover:bg-yellow-400 transition-colors"
              >
                Topics
              </Link>
              <Link
                to="/platforms/"
                className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-800 hover:bg-yellow-400 transition-colors"
              >
                Platforms
              </Link>
              <Link
                to="/submit/"
                className="px-2.5 py-1.5 rounded-md text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition-colors"
              >
                Submit
              </Link>
            </nav>
          </div>
        </div>

        <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl lg:grid lg:grid-cols-2 lg:gap-24">
          <div>
            <div className="mt-12 sm:mt-20">
              <p className="section-eyebrow home-animate-fade-in-up">
                The #1 directory of online communities
              </p>
              <div className="mt-4 sm:max-w-xl">
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-[2.8rem] lg:leading-tight home-animate-fade-in-up home-delay-1">
                  Find your people online.{" "}
                  <span className="border-b-4 border-rose-500 text-3xl">
                    {communityTotalCount.toLocaleString()} communities across every niche.
                  </span>
                </h1>
                <p className="mt-6 text-lg sm:text-xl text-gray-800 max-w-xl leading-relaxed home-animate-fade-in-up home-delay-2">
                  Discover online communities across Reddit, Discord, Slack, Facebook, Circle, forums, and 30+ community platforms.
                </p>
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  const el = document.getElementById("searchfield")
                  const value = el && el.value
                  if (value && value !== "") {
                    navigate(`/search/?keyword=${encodeURIComponent(value)}`)
                  }
                }}
                className="mt-8 sm:max-w-lg sm:w-full sm:flex mb-4 home-animate-fade-in-up home-delay-3"
              >
                <div className="min-w-0 flex-1">
                  <label htmlFor="searchfield" className="sr-only">
                    Search topics and communities
                  </label>
                  <input
                    id="searchfield"
                    name="search"
                    className="block w-full border border-transparent shadow rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 focus:border-rose-500 focus:ring-rose-500"
                    placeholder="Search by topic, name, or platform..."
                    type="search"
                    autoComplete="off"
                    spellCheck="off"
                  />
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-3">
                  <button
                    type="submit"
                    className="block w-full rounded-md border border-transparent px-5 py-3 bg-rose-600 text-base font-medium text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 sm:px-10 transition-colors"
                  >
                    Discover
                  </button>
                </div>
              </form>

              <div className="mt-6 mb-8 home-animate-fade-in-up home-delay-4">
                <Link
                  to="/submit/"
                  className="inline-flex space-x-4 opacity-90 hover:opacity-100"
                >
                  <span className="rounded bg-rose-600 text-white px-2.5 py-1 text-xs font-semibold tracking-wide uppercase">
                    Run a community?
                  </span>
                  <span className="inline-flex text-black items-center text-sm font-medium space-x-1">
                    <span>List it for free</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                </Link>
              </div>

              <div className="hidden sm:flex pb-8 home-animate-fade-in home-delay-4">
                <a
                  href="https://www.producthunt.com/posts/hive-index?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-hive-index"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=321612&theme=light"
                    alt="Hive Index — Featured on Product Hunt"
                    style={{ width: "250px", height: "54px" }}
                    width="250"
                    height="54"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="sm:mx-auto sm:max-w-3xl sm:py-12 lg:py-20">
          <div className="hidden sm:block sm:relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <div className="hidden sm:block">
              <div className="absolute inset-y-0 left-1/2 w-screen bg-yellow-400 rounded-l-3xl lg:left-80 lg:right-0 lg:w-full" />
              <svg
                className="absolute top-8 right-1/2 -mr-3 lg:m-0 lg:left-0"
                width={404}
                height={392}
                fill="none"
                viewBox="0 0 404 392"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="hero-home-pattern-dots"
                    x={0}
                    y={0}
                    width={20}
                    height={20}
                    patternUnits="userSpaceOnUse"
                  >
                    <rect x={0} y={0} width={4} height={4} className="text-yellow-200" fill="currentColor" />
                  </pattern>
                </defs>
                <rect width={404} height={392} fill="url(#hero-home-pattern-dots)" />
              </svg>
            </div>
            <div className="hidden lg:block relative sm:mx-auto sm:max-w-3xl sm:px-0 lg:max-w-none h-full sm:pl-12">
              {!isMobile ? (
                <ScrollingCommunities communities={featuredCommunities} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default HeroSection
